import { customHeaders, requestApi } from 'api/api'
import {
  CreatePortfolioRequest,
  DepositRequest,
  DepositTransferResponse,
  DistributionRequest,
  EarnAssetDetailsResponse,
  EarnAssetTransactionsResponse,
  EarnDashboardResponse,
  EarnStatisticsResponse,
  EarnWalletsRequestParams,
  EarnWalletsResponse,
  Portfolio,
  WithdrawalRequest,
  WithdrawalTransferResponse,
} from 'pages/Earn/types'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

export const getEarnDashboard = async (): Promise<EarnDashboardResponse> => {
  return requestApi({
    url: '/earn/dashboard',
    baseUrl: process.env.VITE_BASE_URL,
    method: 'GET',
  })
}

export const getEarnStatistics = async (): Promise<EarnStatisticsResponse> => {
  return requestApi({
    url: '/earn/statistics',
    baseUrl: process.env.VITE_BASE_URL,
    method: 'GET',
  })
}

export const getEarnAssetDetails = async (
  portfolioId: string
): Promise<EarnAssetDetailsResponse> => {
  return requestApi({
    url: `/earn/asset/${portfolioId}`,
    baseUrl: process.env.VITE_BASE_URL,
    method: 'GET',
  })
}

export const getEarnPortfolio = async (portfolioId: string): Promise<Portfolio> => {
  return requestApi({
    url: `/earn/portfolio/${portfolioId}`,
    baseUrl: process.env.VITE_BASE_URL,
    method: 'GET',
  })
}

export const createEarnPortfolio = async (data: CreatePortfolioRequest): Promise<Portfolio> => {
  return requestApi({
    url: '/earn/portfolio',
    baseUrl: process.env.VITE_BASE_URL,
    method: 'POST',
    data,
    headers: { ...customHeaders, 'Idempotency-Key': data.reference ?? '' },
  })
}

export const depositToEarn = async (data: DepositRequest): Promise<DepositTransferResponse> => {
  return requestApi({
    url: '/earn/deposit',
    baseUrl: process.env.VITE_BASE_URL,
    method: 'POST',
    data,
    headers: { ...customHeaders, 'Idempotency-Key': data.reference },
  })
}

export const withdrawFromEarn = async (
  data: WithdrawalRequest
): Promise<WithdrawalTransferResponse> => {
  return requestApi({
    url: '/earn/withdraw',
    baseUrl: process.env.VITE_BASE_URL,
    method: 'POST',
    data,
    headers: { ...customHeaders, 'Idempotency-Key': data.reference },
  })
}

export const enableCustomerRewardsDistribution = async (
  data: DistributionRequest
): Promise<void> => {
  return requestApi({
    url: '/earn/distribution',
    baseUrl: process.env.VITE_BASE_URL,
    method: 'POST',
    data,
    headers: { ...customHeaders, 'Idempotency-Key': data.reference },
  })
}

export const getEarnWallets = async ({
  currencyCode,
  includeCustomerWallets,
  searchTerm,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
}: EarnWalletsRequestParams): Promise<EarnWalletsResponse> => {
  return requestApi({
    url: '/earn/wallets',
    baseUrl: process.env.VITE_BASE_URL,
    method: 'GET',
    params: {
      ...(size !== undefined && { size }),
      ...(page !== undefined && { page }),
      ...(currencyCode && { currencyCode }),
      ...(searchTerm && { q: searchTerm }),
      ...(includeCustomerWallets !== undefined && { includeCustomerWallets }),
    },
  })
}

export const getEarnAssetTransactions = async (
  portfolioId: string,
  {
    page = API_PAGINATION_OFFSET,
    size = API_PAGINATION_LIMIT,
    start,
    end,
  }: { page?: number; size?: number; start?: string; end?: string } = {}
): Promise<EarnAssetTransactionsResponse> => {
  return requestApi({
    url: `/earn/asset/${portfolioId}/transactions`,
    baseUrl: process.env.VITE_BASE_URL,
    method: 'GET',
    params: {
      walletReference: portfolioId,
      page,
      size,
      categories: 'EARNING',
      ...(start && { start }),
      ...(end && { end }),
    },
  })
}
